import {
  getAwardRestrictionsOperations,
  getEmailRequiredConstraintOperations,
  getFulfillmentChannelConstraintOperations,
  getFulfillmentWindowOperations,
  getMinimumPurchaseConstraintOperations,
  getPaymentMethodConstraintOperations,
  makeRequiresActivationConstraintOperations,
} from '@occc/feat-constraints';
import { getLimitsInConstraintOperations, LimitType } from '@occc/feat-limits';
import { AwardType, GetConfigureFormOperations, Operation } from '@occc-app/offerHelpers/types';
import { createOperationToDisableTriggerOnProgress } from '@occc-app/routes/CampaignComponents/components/TriggerOnProgress/utils';
import { getOfferCustomDataOperation } from '@occc-app/offerHelpers/operations/getOfferCustomDataOperation';
import { makeOfferUpdateOperation } from '@occc-app/offerHelpers/operations/makeOfferUpdateOperation';
import { Offer_Engagement_Type } from '@occc/graphql';
import { requiresActivationApplicableOfferTypes } from '@occc-app/routes/Offer/components/OfferConfigureForm/requiresActivationApplicableOfferTypes';
import { getRequiresActivationConstraintAndLimitsDeleteOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/getRequiresActivationConstraintAndLimitsDeleteOperations';
import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getOfferCampaignUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerCampaignUpdateOperations';
import { getOfferFulfillmentPriorityUpdateOperations } from '@occc-app/offerHelpers/operations/getOfferFulfillmentPriorityUpdateOperations';
import { getScanDataConfigurationOperation } from '@occc-app/offerHelpers/operations/getScanDataConfigurationOperation';
import { getEarningMechanismOperations } from './earningMechanismOperations';
import {
  getIsTransactionFrequencyLimitSupported,
  getIsTransactionLimitSupported,
} from './offer.utils';

import { minimumPurchaseApplicableOfferTypes } from '../../../constants';
import { OfferConfigureFormValues, OfferType } from '../../../types';

export const getConfigureFormOperations: GetConfigureFormOperations<OfferConfigureFormValues> = (
  offerId,
  offer,
  values,
  dirtyFields,
) => {
  const earningMechanismOperations = getEarningMechanismOperations(offerId, values, dirtyFields);

  const isTransactionLimitSupported = getIsTransactionLimitSupported(values.offerType);

  const awardCountLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.awardCount,
    dirtyFields,
    LimitType.AwardCount,
    {
      isTransactionLimitSupported,
      ...(offer
        ? {
            isTransactionFrequencyLimitSupported: getIsTransactionFrequencyLimitSupported(
              values.offerType,
            ),
            offer,
          }
        : {}),
    },
  );

  const fulfillmentWindowOperations = getFulfillmentWindowOperations(
    offerId,
    values.fulfillmentWindow,
    dirtyFields,
  );

  const minimumPurchaseConstraintOperations =
    values.offerType && minimumPurchaseApplicableOfferTypes.includes(values.offerType)
      ? getMinimumPurchaseConstraintOperations(offerId, values, dirtyFields, offer)
      : getMinimumPurchaseConstraintOperations(
          offerId,
          {
            minimumPurchaseConstraints: {
              isEnabled: false,
              items: [],
            },
          },
          dirtyFields,
          offer,
        );

  const paymentMethodConstraintOperations =
    values.awardType === AwardType.AwardPointsPer
      ? []
      : getPaymentMethodConstraintOperations(offerId, offer, values, dirtyFields);

  const emailRequiredConstraintOperations = values.offerType
    ? getEmailRequiredConstraintOperations(offerId, offer, values, dirtyFields, values.offerType)
    : [];

  const fulfillmentChannelConstraintOperations = getFulfillmentChannelConstraintOperations(
    offerId,
    offer,
    values,
  );

  const additionalOperations: Operation[] = [];

  const isOverTimeEarningMechanism = values.offerType === OfferType.SpendBonus;

  const currentEngagementType = offer?.engagementType;
  const newEngagementType =
    values.offerType === OfferType.ExternalFulfillment
      ? Offer_Engagement_Type.ExternallyFulfillable
      : Offer_Engagement_Type.Transactional;

  if (currentEngagementType !== newEngagementType) {
    additionalOperations.push(
      makeOfferUpdateOperation(offerId, {
        engagementType: newEngagementType,
      }),
    );
  }

  additionalOperations.push(
    ...getOfferNameAndDescriptionUpdateOperations(offerId, values, dirtyFields),
  );

  additionalOperations.push(...getOfferCampaignUpdateOperations(offerId, values, dirtyFields));

  additionalOperations.push(
    ...getOfferFulfillmentPriorityUpdateOperations(offerId, values, dirtyFields),
  );

  // https://stuzo.atlassian.net/browse/OCA-1516
  // when an offer subtype is changed to a non-Progress EM
  // set "triggerOnProgress" to "false" if needed
  if (
    offer?.earningMechanism?.__typename === 'EarningMechanismTotalSpendOverTime' &&
    !isOverTimeEarningMechanism &&
    offer?.triggerPreferences.triggerOnProgress
  ) {
    const operation = createOperationToDisableTriggerOnProgress(offerId, offer.triggerPreferences);
    additionalOperations.push(operation);
  }

  const customDataOperation = getOfferCustomDataOperation({
    dirtyFields,
    values,
    offerId,
  });
  if (customDataOperation) {
    additionalOperations.push(customDataOperation);
  }

  const scanDataConfigurationOperation = getScanDataConfigurationOperation({
    offerId,
    values,
    dirtyFields,
  });
  if (scanDataConfigurationOperation) {
    additionalOperations.push(scanDataConfigurationOperation);
  }

  const activationConstraintOperations = makeRequiresActivationConstraintOperations(
    offerId,
    values.activation,
    dirtyFields,
    offer,
  );
  const activationLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.activation,
    dirtyFields,
    LimitType.Activation,
  );
  const activationConstraintAndLimitsOperation = [
    ...activationConstraintOperations,
    ...activationLimitsOperations,
  ];

  const requiresActivationConstraintAndLimitsDeleteOperations =
    getRequiresActivationConstraintAndLimitsDeleteOperations(values.activation);

  const awardRestrictionsOperations = offer
    ? getAwardRestrictionsOperations({
        offer,
        values: values.awardRestrictions,
        dirtyFields,
        forceDelete:
          values.offerType === OfferType.ExternalFulfillment ||
          values.awardType !== AwardType.AwardVouchers,
      })
    : [];

  const isActivationApplicable =
    !!values.offerType && requiresActivationApplicableOfferTypes.includes(values.offerType);

  const activationAndConstraintOperations = isActivationApplicable
    ? activationConstraintAndLimitsOperation
    : requiresActivationConstraintAndLimitsDeleteOperations;

  // ORDER is important, can not mix awardCount limits and constraint
  const orderedAwardCountLimitsAndActivationConstraintAndLimits = values.activation.isEnabled
    ? [...awardCountLimitsOperations, ...activationAndConstraintOperations]
    : [...activationAndConstraintOperations, ...awardCountLimitsOperations];

  return [
    ...additionalOperations,
    ...earningMechanismOperations,
    ...fulfillmentWindowOperations,
    ...minimumPurchaseConstraintOperations,
    ...orderedAwardCountLimitsAndActivationConstraintAndLimits,
    ...paymentMethodConstraintOperations,
    ...emailRequiredConstraintOperations,
    ...fulfillmentChannelConstraintOperations,
    ...awardRestrictionsOperations,
  ];
};
